/**
 * Intercom Service
 * Centralized wrapper for the official Intercom Messenger SDK.
 */

import {
    Intercom as initializeIntercom,
    boot,
    hide,
    shutdown,
    show,
    showArticle as showIntercomArticle,
    trackEvent,
    update
} from '@intercom/messenger-js-sdk';

const APP_ID = 'xp9iy0t4';
const API_BASE = 'https://api-iam.intercom.io';

export type IntercomCompany = {
    company_id: string;
};

export type IntercomBootSettings = {
    user_id?: string;
    email?: string;
    name?: string;
    role?: string;
    companies?: IntercomCompany[];
};

export type IntercomUpdateSettings = IntercomBootSettings & {
    custom_launcher_selector?: string | null;
    hide_default_launcher?: boolean;
};

class IntercomService {
    private initialized = false;
    private booted = false;

    getMobileDisplaySettings(): Pick<IntercomUpdateSettings, 'custom_launcher_selector' | 'hide_default_launcher'> {
        if (typeof window !== 'undefined' && window.matchMedia('only screen and (max-width: 760px)').matches) {
            return {
                custom_launcher_selector: '#intercom_custom_launcher',
                hide_default_launcher: true
            };
        }

        return {
            custom_launcher_selector: null,
            hide_default_launcher: false
        };
    }

    boot(settings: IntercomBootSettings): void {
        this.ensureInitialized();
        boot({
            app_id: APP_ID,
            api_base: API_BASE,
            ...this.getMobileDisplaySettings(),
            ...settings
        });
        this.booted = true;
    }

    update(settings: IntercomUpdateSettings): void {
        this.ensureInitialized();
        update(settings);
    }

    updateDisplayForViewport(): void {
        this.update(this.getMobileDisplaySettings());
    }

    trackEvent(eventName: string, metadata?: Record<string, unknown>): void {
        this.ensureInitialized();
        trackEvent(eventName, metadata);
    }

    show(): void {
        this.ensureInitialized();
        show();
    }

    hide(): void {
        this.ensureInitialized();
        hide();
    }

    /**
     * Opens a Help Center article inside the messenger.
     *
     * Deliberately does not call ensureInitialized(): booting the messenger just to
     * show an article would create a session with no user context. When the messenger
     * is not already up the caller should fall back to the public docs site.
     *
     * Checks `booted` rather than `initialized`: the latter only means the loader
     * snippet ran, and the SDK silently queues showArticle into window.intercomSettings
     * when the messenger has not booted — the user would click and see nothing.
     *
     * @returns false when the article could not be shown.
     */
    showArticle(articleId: string): boolean {
        if (typeof window === 'undefined' || !this.booted) {
            return false;
        }

        try {
            showIntercomArticle(articleId);
            return true;
        } catch {
            return false;
        }
    }

    shutdown(): void {
        if (!this.initialized) {
            return;
        }

        shutdown();
        this.initialized = false;
        this.booted = false;
    }

    private ensureInitialized(): void {
        if (this.initialized || typeof window === 'undefined') {
            return;
        }

        initializeIntercom({
            app_id: APP_ID,
            api_base: API_BASE
        });
        this.initialized = true;
    }
}

export const intercomService = new IntercomService();

export default IntercomService;
